import {
    type PointerEvent as ReactPointerEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

// Lets the floating chat launcher be dragged for the current view. The dragged
// position is intentionally ephemeral: it is never persisted, so a reload
// starts back at the default corner. A width change snaps it back too; a
// height-only resize can be the mobile keyboard opening and must preserve the
// user's drag so the launcher stays clear of the composer.

// Pointer travel (px) before a press is treated as a drag rather than a click,
// so a normal click still opens the chat.
const DRAG_THRESHOLD_PX = 4;
// Keep the launcher this far from the viewport edges while dragging.
const EDGE_MARGIN_PX = 8;
const DEFAULT_LAUNCHER_SIZE_PX = 48;

export type LauncherPosition = {
    readonly left: number;
    readonly top: number;
};

export type DraggableLauncher = {
    // `null` => render at the default corner; otherwise pin to {left, top}.
    readonly position: LauncherPosition | null;
    readonly isDragging: boolean;
    readonly onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
    readonly onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
    readonly onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
    readonly onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
    // Returns true (consuming the flag) when the click that follows a drag
    // should be ignored, so dragging the launcher never toggles the chat.
    readonly consumeDragClick: () => boolean;
};

type DragState = {
    readonly pointerId: number;
    readonly startX: number;
    readonly startY: number;
    readonly originLeft: number;
    readonly originTop: number;
};

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

function maxOffset(viewport: number, size: number): number {
    return Math.max(EDGE_MARGIN_PX, viewport - size - EDGE_MARGIN_PX);
}

export function useDraggableLauncherPosition(): DraggableLauncher {
    // Always starts at the default corner; never restored from storage.
    const [position, setPosition] = useState<LauncherPosition | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const dragRef = useRef<DragState | null>(null);
    // True once the active press has moved past the drag threshold.
    const movedRef = useRef(false);
    // Set on drag-end so the synthetic click that follows pointerup is ignored.
    const suppressClickRef = useRef(false);
    const sizeRef = useRef(DEFAULT_LAUNCHER_SIZE_PX);
    const hasCustomPosition = position !== null;

    // A width change can invalidate the dragged position. Height-only changes
    // are often caused by the mobile keyboard, so keep the user's placement.
    useEffect(() => {
        if (!hasCustomPosition) {
            return;
        }

        const viewportWidth = window.innerWidth;
        function resetToDefault() {
            if (window.innerWidth !== viewportWidth) {
                setPosition(null);
            }
        }

        window.addEventListener('resize', resetToDefault);
        return () => {
            window.removeEventListener('resize', resetToDefault);
        };
    }, [hasCustomPosition]);

    const onPointerDown = useCallback(
        (event: ReactPointerEvent<HTMLElement>) => {
            if (event.button !== 0) {
                return;
            }
            // Clear any flag left by a drag that ended off the launcher.
            suppressClickRef.current = false;
            movedRef.current = false;
            const rect = event.currentTarget.getBoundingClientRect();
            sizeRef.current = rect.width;
            dragRef.current = {
                pointerId: event.pointerId,
                startX: event.clientX,
                startY: event.clientY,
                originLeft: rect.left,
                originTop: rect.top,
            };
            event.currentTarget.setPointerCapture(event.pointerId);
        },
        [],
    );

    const onPointerMove = useCallback(
        (event: ReactPointerEvent<HTMLElement>) => {
            const drag = dragRef.current;
            if (drag === null || event.pointerId !== drag.pointerId) {
                return;
            }
            const deltaX = event.clientX - drag.startX;
            const deltaY = event.clientY - drag.startY;
            if (
                !movedRef.current &&
                Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD_PX
            ) {
                return;
            }
            movedRef.current = true;
            setIsDragging(true);
            const size = sizeRef.current;
            setPosition({
                left: clamp(
                    drag.originLeft + deltaX,
                    EDGE_MARGIN_PX,
                    maxOffset(window.innerWidth, size),
                ),
                top: clamp(
                    drag.originTop + deltaY,
                    EDGE_MARGIN_PX,
                    maxOffset(window.innerHeight, size),
                ),
            });
        },
        [],
    );

    const onPointerUp = useCallback((event: ReactPointerEvent<HTMLElement>) => {
        const drag = dragRef.current;
        if (drag === null || event.pointerId !== drag.pointerId) {
            return;
        }
        dragRef.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        if (!movedRef.current) {
            return;
        }
        suppressClickRef.current = true;
        setIsDragging(false);
    }, []);

    // The browser can abort a drag (e.g. a touch is interrupted) with
    // pointercancel and no following pointerup; reset state so the launcher
    // doesn't stay stuck showing the grabbing cursor.
    const onPointerCancel = useCallback(
        (event: ReactPointerEvent<HTMLElement>) => {
            const drag = dragRef.current;
            if (drag === null || event.pointerId !== drag.pointerId) {
                return;
            }
            dragRef.current = null;
            movedRef.current = false;
            setIsDragging(false);
        },
        [],
    );

    const consumeDragClick = useCallback(() => {
        if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return true;
        }
        return false;
    }, []);

    return {
        position,
        isDragging,
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onPointerCancel,
        consumeDragClick,
    };
}
